import { useEffect, useState } from "react";
import { X, WifiHigh } from "@phosphor-icons/react";

const ANCHORS = ["/newsroom/anchor2.png", "/newsroom/anchor1.png"];
const T_POP = 750;   // scale up from zero, then stays.
const T_SWAP = 5000; // switch anchor image every 5s.

// A 3D pop-out broadcast screen (no TV chassis) that scales from zero into the
// Central Newsroom masthead's center negative space, stays live, and cycles
// between anchor images every few seconds with a crossfade.
export function NewsroomTV() {
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem("nc_tv_dismissed") === "1"; } catch { return false; }
  });
  const [popped, setPopped] = useState(false);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (dismissed) return;
    const t = setTimeout(() => setPopped(true), 120);
    return () => clearTimeout(t);
  }, [dismissed]);

  useEffect(() => {
    if (dismissed || !popped) return;
    const iv = setInterval(() => setIdx((i) => (i + 1) % ANCHORS.length), T_SWAP);
    return () => clearInterval(iv);
  }, [dismissed, popped]);

  if (dismissed) return null;

  const dismiss = () => {
    try { sessionStorage.setItem("nc_tv_dismissed", "1"); } catch { /* ignore */ }
    setDismissed(true);
  };

  return (
    <div className="pointer-events-none relative z-[6] w-full h-full flex items-center justify-center"
         data-testid="newsroom-tv-wrap" aria-hidden>
      <div className="tv-stage" style={{ perspective: "1000px", height: "100%" }}>
        <div
          className="tv-screen pointer-events-auto"
          data-testid="newsroom-tv"
          style={{
            transform: popped ? "scale(1) rotateY(-6deg) rotateX(3deg)" : "scale(0.02) rotateY(-6deg) rotateX(3deg)",
            transition: `transform ${T_POP}ms cubic-bezier(.18,1.5,.4,1)`,
          }}
        >
          <button onClick={dismiss} data-testid="newsroom-tv-dismiss" title="Dismiss"
            className="absolute -top-2 -right-2 z-20 w-6 h-6 rounded-full bg-zinc-900 text-white/80 border border-white/20 shadow-lg flex items-center justify-center hover:bg-rose-600 hover:text-white transition-colors pointer-events-auto">
            <X size={12} weight="bold" />
          </button>

          <div className="tv-picture">
            {ANCHORS.map((src, i) => (
              <img key={src} src={src} alt="" draggable={false}
                className="absolute inset-0 w-full h-full object-cover"
                style={{
                  opacity: i === idx ? 1 : 0,
                  transition: "opacity 600ms ease",
                  animation: i === idx ? "tvZoom 5.4s ease-out both" : "none",
                }} />
            ))}
            <div className="tv-scanlines" />
            <div className="tv-vignette" />
            <div className="absolute top-1.5 left-1.5 z-10 flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-rose-600/90 backdrop-blur-sm">
              <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
              <span className="text-[8px] font-black uppercase tracking-widest text-white leading-none">Live</span>
            </div>
            <div className="absolute top-1.5 right-1.5 z-10 text-white/70"><WifiHigh size={11} weight="bold" /></div>
            <div className="tv-ticker">
              <span>NEET COUNSELLING&nbsp;·&nbsp;RESULTS&nbsp;·&nbsp;SEAT MATRIX&nbsp;·&nbsp;CUTOFFS&nbsp;·&nbsp;SCHEDULES&nbsp;·&nbsp;</span>
              <span>NEET COUNSELLING&nbsp;·&nbsp;RESULTS&nbsp;·&nbsp;SEAT MATRIX&nbsp;·&nbsp;CUTOFFS&nbsp;·&nbsp;SCHEDULES&nbsp;·&nbsp;</span>
            </div>
          </div>
          <div className="tv-glass" />
        </div>
      </div>

      <style>{`
        .tv-screen{
          position:relative; height:100%; aspect-ratio:16/9; border-radius:10px; overflow:hidden;
          background:#05060a; transform-style:preserve-3d; will-change:transform;
          box-shadow:0 14px 30px -10px rgba(0,0,0,.45),inset 0 0 0 2px rgba(255,255,255,.08),inset 0 0 18px rgba(0,0,0,.6);
        }
        .tv-picture{position:absolute; inset:0; overflow:hidden}
        .tv-scanlines{position:absolute; inset:0; pointer-events:none; z-index:5;
          background:repeating-linear-gradient(to bottom,rgba(0,0,0,.20) 0 1px,transparent 1px 3px);
          mix-blend-mode:multiply; animation:tvScan 6s linear infinite;}
        .tv-vignette{position:absolute; inset:0; pointer-events:none; z-index:5;
          background:radial-gradient(ellipse at center,transparent 58%,rgba(0,0,0,.5) 100%);}
        .tv-glass{position:absolute; inset:0; pointer-events:none; border-radius:10px;
          background:linear-gradient(120deg,rgba(255,255,255,.14),transparent 32%,transparent 72%,rgba(255,255,255,.05));}
        .tv-ticker{position:absolute; left:0; right:0; bottom:0; height:15px; overflow:hidden; z-index:10;
          background:linear-gradient(90deg,#4c1d95,#be123c); display:flex; white-space:nowrap;}
        .tv-ticker span{display:inline-block; padding-top:2px; font-size:7.5px; font-weight:900;
          letter-spacing:.08em; color:#fff; animation:tvTick 12s linear infinite;}
        @keyframes tvZoom{0%{transform:scale(1.16) translateY(2%)}100%{transform:scale(1) translateY(0)}}
        @keyframes tvScan{0%{background-position:0 0}100%{background-position:0 60px}}
        @keyframes tvTick{0%{transform:translateX(0)}100%{transform:translateX(-100%)}}
        @media (prefers-reduced-motion:reduce){
          .tv-scanlines,.tv-ticker span{animation:none}
          .tv-picture img{animation:none !important}
        }
      `}</style>
    </div>
  );
}
